import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Tooltip,
  Alert,
  TextField,
  MenuItem,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import DeleteIcon from "@mui/icons-material/Delete";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import {
  getContactRequests,
  markContactRequestSeen,
  deleteContactRequest,
} from "../../../Services/ContactUsAPi";

/*
  ContactFormpage: صفحة عرض طلبات تواصل الوارد
*/
const ContactFormpage = () => {
  const [requests, setRequests] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [readFilter, setReadFilter] = useState("all"); // "all" | "read" | "unread"
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await getContactRequests();
        // service returns body like { message, data: [...] }
        const list = res?.data ?? res?.data ?? res;
        // defensively extract array
        const arr = (res && res.data) ? res.data : (Array.isArray(res) ? res : (res?.data ?? []));
        if (mounted) {
          // رتب من الأحدث إلى الأقدم حسب created_at ثم خزّن النتيجة
          const sorted = Array.isArray(arr)
            ? arr.slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
            : arr;
          setRequests(sorted);
          setFilteredRequests(sorted); // املأ المفلترة بالقيم الافتراضية
        }
      } catch (err) {
        console.error("getContactRequests error:", err);
        setError(err?.response?.data?.message || err?.message || "فشل جلب الطلبات");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  // فلترة حسب نص البحث عبر جميع الحقول المهمة
  useEffect(() => {
    const q = (searchTerm || "").toString().trim().toLowerCase();
    const tenantFiltered = requests.filter((r) => {
      if (readFilter === "read" && Number(r.seen) !== 1) return false;
      if (readFilter === "unread" && Number(r.seen) === 1) return false;
      return true;
    });

    if (!q) {
      setFilteredRequests(tenantFiltered);
      return;
    }
    const filtered = tenantFiltered.filter((r) => {
      const values = [
        r.id,
        r.name,
        r.email,
        r.phone,
        r.country,
        r.city,
        r.message,
        r.seen,
        r.created_at,
        r.updated_at,
      ];
      return values.some((v) => (v ?? "").toString().toLowerCase().includes(q));
    });
    setFilteredRequests(filtered);
  }, [requests, searchTerm, readFilter]);

  const formatDate = (d) => {
    if (!d) return "-";
    try {
      const dt = new Date(d);
      return dt.toLocaleString("ar-EG");
    } catch {
      return d;
    }
  };

  const handleMarkSeen = async (r) => {
    const id = r.id;
    if (!id) return;
    try {
      setActionLoadingId(id);
      await markContactRequestSeen(id);
      setRequests((prev) => prev.map((it) => (it.id === id ? { ...it, seen: 1 } : it)));
    } catch (err) {
      console.error("markContactRequestSeen error:", err);
      setError(err?.response?.data?.message || err?.message || "فشل تحديث الحالة");
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleDelete = async (r) => {
    const id = r.id;
    if (!id) return;
    if (!window.confirm(`هل تريد حذف طلب التواصل #${id}؟`)) return;
    try {
      setActionLoadingId(id);
      await deleteContactRequest(id);
      setRequests((prev) => prev.filter((it) => it.id !== id));
    } catch (err) {
      console.error("deleteContactRequest error:", err);
      setError(err?.response?.data?.message || err?.message || "فشل الحذف");
    } finally {
      setActionLoadingId(null);
    }
  };

  // تصدير البيانات إلى CSV (يفتحها Excel)
  const exportToExcel = () => {
    const src = filteredRequests && filteredRequests.length ? filteredRequests : requests;
    if (!src || !src.length) return;
    const headers = ["id", "name", "email", "phone", "country", "city", "message", "seen", "created_at", "updated_at"];
    const escape = (v) => {
      if (v === null || v === undefined) return "";
      const s = String(v);
      // هروب علامات الاقتباس المزدوجة
      return `"${s.replace(/"/g, '""')}"`;
    };
    const rows = src.map((r) =>
      headers.map((h) => escape(h === "message" ? (r.message ?? "") : (r[h] ?? r?.[h] ?? ""))).join(",")
    );
    const csv = [headers.map((h) => `"${h}"`).join(","), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const date = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `contact_requests_${date}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            طلبات التواصل الواردة
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", width: "560px" }}>
          <TextField
            size="small"
            placeholder="ابحث في الطلبات (اسم، بريد، جوال، رسالة، معرف...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
          />
          <TextField
            size="small"
            select
            value={readFilter}
            onChange={(e) => setReadFilter(e.target.value)}
            sx={{ width: 160 }}
          >
            <MenuItem value="all">كل الحالات</MenuItem>
            <MenuItem value="unread">جديد فقط</MenuItem>
            <MenuItem value="read">مقروء فقط</MenuItem>
          </TextField>
          <Button variant="outlined" size="small" startIcon={<FileDownloadIcon />} onClick={exportToExcel}>
            تصدير إلى Excel
          </Button>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      ) : (
        <Paper sx={{ p: 1 }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell align="right">#</TableCell>
                  <TableCell align="right">الاسم</TableCell>
                  <TableCell align="right">البريد</TableCell>
                  <TableCell align="right">الجوال</TableCell>
                  <TableCell align="right">حالة القراءة</TableCell>
                  <TableCell align="right">تاريخ الإنشاء</TableCell>
                  <TableCell align="right">إجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">لا توجد طلبات</TableCell>
                  </TableRow>
                ) : (
                  filteredRequests.map((r) => (
                    <TableRow key={r.id} sx={{ backgroundColor: r.seen ? "inherit" : "#fff7e6" }}>
                      <TableCell align="right">{r.id}</TableCell>
                      <TableCell align="right">{r.name}</TableCell>
                      <TableCell align="right">{r.email}</TableCell>
                      <TableCell align="right">{r.phone}</TableCell>
                      <TableCell align="right">
                        {r.seen ? <Typography variant="body2">مقروء</Typography> : <Typography variant="body2" color="warning.main">جديد</Typography>}
                      </TableCell>
                      <TableCell align="right">{formatDate(r.created_at)}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="عرض التفاصيل">
                          <IconButton size="small" onClick={() => setSelected(r)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="تمييز كمقروء">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleMarkSeen(r)}
                              disabled={actionLoadingId === r.id || r.seen === 1}
                            >
                              <DoneAllIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(r)}
                              disabled={actionLoadingId === r.id}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* تفاصيل الرسالة */}
      <Dialog open={Boolean(selected)} onClose={() => setSelected(null)} fullWidth maxWidth="sm">
        <DialogTitle>تفاصيل طلب التواصل #{selected?.id}</DialogTitle>
        <DialogContent dividers>
          {selected && (
            <Box>
              <Typography><strong>الاسم:</strong> {selected.name}</Typography>
              <Typography><strong>البريد:</strong> {selected.email}</Typography>
              <Typography><strong>الجوال:</strong> {selected.phone}</Typography>
              <Typography sx={{ mt: 2 }}><strong>الرسالة:</strong></Typography>
              <Typography sx={{ whiteSpace: "pre-wrap" }}>{selected.message}</Typography>
              <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
                <Button variant="outlined" onClick={() => { handleMarkSeen(selected); }}>تمييز كمقروء</Button>
                <Button variant="outlined" color="error" onClick={() => { handleDelete(selected); setSelected(null); }}>حذف</Button>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ContactFormpage;
